<template>
  <RenderWithDependency
    :dependency="csdService"
    class="bg-primary-100 py-25 lg:py-30 xl:py-40 paragraph--padded"
  >
    <Container>
      <!-- Header -->
      <h2
        :id="slugify(csdService?.name)"
        class="font-bold text-4xl break-words hyphens-auto text-primary-600 mb-20"
      >
        {{ csdService?.name }}
      </h2>

      <!-- Short Description -->
      <p
        v-if="csdService?.fieldCsdShortDescription"
        class="mb-20 break-words"
        v-html="csdService.fieldCsdShortDescription"
      />

      <!-- Button -->
      <DrupalLink
        v-if="buttonUrl"
        :to="buttonUrl"
        class="button is-super mt-30 mb-50 is-link"
      >
        {{ buttonTitle }}
      </DrupalLink>

      <!-- Fees header -->
      <h3 v-if="serviceFees.length" class="text-2xl font-bold mb-30">
        {{ $texts('csd.fees', 'Gebühren') }}
      </h3>

      <!-- Service Fees table -->
      <table v-if="serviceFees.length" class="border-t-2 xl:w-9/12 mb-50">
        <caption class="sr-only">
          {{
            $texts('csd.fees', 'Gebühren')
          }}
        </caption>
        <tr
          v-for="serviceFee in serviceFees"
          :key="serviceFee.id"
          class="border-b align-top"
        >
          <td
            class="text-xs py-5 pr-10 block md:table-cell"
            :class="{
              'md:w-1/4': options.serviceFeesTableLayout === '25_75',
              'md:w-1/2': options.serviceFeesTableLayout === '50_50',
              'md:w-3/4': options.serviceFeesTableLayout === '75_25',
            }"
          >
            {{ serviceFee.item }}
          </td>
          <td
            class="block md:table-cell text-lg font-bold py-5"
            v-html="serviceFee.fee"
          />
        </tr>
      </table>

      <!-- Prerequisites -->
      <Accordion
        v-if="csdService?.fieldCsdPrerequisites"
        :title="$texts('csd.prerequisites', 'Vorbedingungen')"
        class="mb-10 break-words"
      >
        <p v-html="csdService.fieldCsdPrerequisites" />
      </Accordion>

      <!-- Duration -->
      <Accordion
        v-if="csdService?.fieldCsdDuration"
        :title="$texts('csd.duration', 'Dauer')"
        class="mb-10 break-words"
      >
        <p v-html="csdService.fieldCsdDuration" />
      </Accordion>

      <!-- Required documents -->
      <Accordion
        v-if="csdService?.fieldCsdRequiredDocuments"
        :title="$texts('csd.requiredDocuments', 'Erforderliche Dokumente')"
        class="mb-10 break-words"
      >
        <p v-html="csdService.fieldCsdRequiredDocuments" />
      </Accordion>

      <!-- Legal basis -->
      <Accordion
        v-if="csdService?.fieldCsdLegalBasis"
        :title="$texts('csd.legalBasis', 'Rechtliche Grundlage')"
        class="mb-10 break-words"
      >
        <p v-html="csdService.fieldCsdLegalBasis" />
      </Accordion>

      <!-- Legal basis -->
      <Accordion
        v-if="csdService?.spaDepartment"
        :title="
          $texts(
            'csd.legalResponsibleServiceProvider',
            'Gesetzlicher zuständiger Leistungserbringer',
          )
        "
        class="mb-10 break-words"
      >
        <address class="not-italic">
          <strong class="mb-20 block">{{ csdService.spaDepartment }}</strong>

          <div v-if="csdService.spaOrgUnit">{{ csdService.spaOrgUnit }}</div>
          <div v-if="csdService.spaFurtherClass">
            {{ csdService.spaFurtherClass }}
          </div>

          <div v-if="csdService.spaStreet">
            {{ csdService.spaStreet }}
            <template v-if="csdService.spaStreetNumber">
              {{ csdService.spaStreetNumber }}
            </template>
          </div>

          <div
            v-if="csdService.spaPostalCode && csdService.spaLocation"
            class="mb-20"
          >
            {{ csdService.spaPostalCode }} {{ csdService.spaLocation }}
          </div>

          <a
            v-if="csdService.spaPhone"
            :href="preparedPhoneNumber"
            class="block"
          >
            {{ csdService.spaPhone }}
          </a>

          <a
            v-if="csdService.spaEmail"
            :href="'mailto:' + csdService.spaEmail"
            class="block"
          >
            {{ csdService.spaEmail }}
          </a>

          <NuxtLink
            v-if="preparedWebsiteUrl"
            target="_blank"
            :to="preparedWebsiteUrl"
          >
            {{ preparedWebsiteUrl }}
            <span class="sr-only">
              {{
                $texts(
                  'accessibility.externalLinkText',
                  'Externer Link, wird in einem neuen Tab oder Fenster geöffnet',
                )
              }}
            </span>
          </NuxtLink>
        </address>
      </Accordion>
    </Container>
  </RenderWithDependency>
</template>

<script lang="ts" setup>
import { slugify } from '~/helpers/slugify'
import type {
  ParagraphCsdServiceFragment,
  TaxonomyTermCsdFragment,
} from '#graphql-operations'

type ServiceFee = {
  id?: string
  item?: string
  fee?: string
}

const { options } = defineBlokkli({
  bundle: 'csd_service',
  globalOptions: ['bkVisibleLanguages'],
  chunkName: 'rare',
  options: {
    serviceFeesTableLayout: {
      type: 'radios',
      label: 'Layout der Tabelle',
      default: '25_75',
      displayAs: 'grid',
      options: {
        '25_75': { columns: [1, 3], label: '25% / 75%' },
        '50_50': { columns: [1, 1], label: '50% / 50%' },
        '75_25': { columns: [3, 1], label: '75% / 25%' },
      },
    },
    showInToc: {
      type: 'checkbox',
      label: 'Link in der Navigation anzeigen',
      default: false,
    },
  },
})

const { $texts } = useNuxtApp()

const props = defineProps<{
  id: ParagraphCsdServiceFragment['id']
  csdService?: TaxonomyTermCsdFragment
}>()

const buttonUrl = computed(() => {
  return props.csdService?.fieldCsdWebBsLink?.uri?.path
})

const buttonTitle = computed(() => {
  return props.csdService?.fieldCsdWebBsLink?.title
})

const serviceFees = computed<ServiceFee[]>(() => {
  if (!props.csdService?.fieldCsdFees) {
    return []
  }
  return props.csdService?.fieldCsdFees.map((csdServiceFee) => {
    return {
      id: csdServiceFee.fieldId,
      item: csdServiceFee.fieldCsdSfFeeItem,
      fee: csdServiceFee.fieldCsdSfSingleFee,
    }
  })
})

const preparedPhoneNumber = computed(() => {
  if (!props.csdService?.spaPhone) {
    return ''
  }

  return 'tel:' + props.csdService.spaPhone.replaceAll(' ', '')
})

const preparedWebsiteUrl = computed(() => {
  if (!props.csdService?.spaWebAddress) {
    return ''
  }

  const url = props.csdService.spaWebAddress.replace('http://', 'https://')

  // Add https to URL.
  if (!url.startsWith('https')) {
    return 'https://' + url
  }

  return url
})

defineOptions({
  name: 'ParagraphCsdService',
})
</script>
